'use client';

import { useEffect } from 'react';
import { useProxyStore } from '../store/proxy-store';
import { useElectronAPI } from './useElectronAPI';
import type { MtlsEntry } from '../electron/proxy-types';

/**
 * Pushes the user's enabled "Client TLS" settings entries into the Electron
 * proxy's mTLS registry whenever they actually change.
 *
 * This must live at the persistent layout level (not inside the settings tab)
 * so that opening/closing the Client TLS tab doesn't re-sync. The Client TLS
 * tab is rendered inside a `Tabs.Root` configured with `lazyMount` +
 * `unmountOnExit`, so a tab-scoped sync would re-fire on every visit and stomp
 * on the launcher's per-session "Launch Browser (no client TLS)" override,
 * which intentionally clears settings entries for the lifetime of a Chromium
 * instance.
 *
 * `setMtlsConfig` is a stable `useCallback` from `useElectronAPI` and the
 * Zustand selector returns the same array reference until entries actually
 * change, so the effect only fires when the user edits the settings.
 */
export function useSyncMtlsSettings(): void {
  const { setMtlsConfig } = useElectronAPI();
  const storeEntries = useProxyStore(
    (state) => state.settings.clientTlsCertificates,
  );

  useEffect(() => {
    const entries: MtlsEntry[] = storeEntries
      .filter((entry) => entry.enabled)
      .map((entry) => ({
        id: entry.id,
        origin: entry.origin,
        cert: entry.cert ?? null,
        key: entry.key ?? null,
        pfx: entry.pfx ?? null,
        passphrase: entry.passphrase ?? null,
        source: entry.source,
        enabled: entry.enabled,
      }));
    setMtlsConfig(entries).catch(() => undefined);
  }, [setMtlsConfig, storeEntries]);
}
